// Ricerca predittiva v2: livelli dal pannello reale + stazioni/comuni.
// I due percorsi restano distinti: la lente nell'header trova e attiva i livelli,
// il campo nel pannello localizza stazioni e comuni sulla mappa.
import { state as stationsState } from '../layers/stations.js';
import { stationNetworkFullLabel } from '../lib/params.js';

function norm(s) {
    return (s || '')
        .toString()
        .replace(/([a-zà-ÿ])([A-ZÀ-ß])/g, '$1 $2')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}
function compactNorm(s) { return norm(s).replace(/\s+/g, ''); }

function scoreStation(q, st) {
    const nq = norm(q);
    const cq = compactNorm(q);
    if (!nq) return 0;
    let best = 0;
    const fields = [
        { txt: norm(st.name), w: 1.0 },
        { txt: norm(st.comune), w: 0.6 },
        { txt: norm(st.rete), w: 0.4 },
    ];
    for (const f of fields) {
        if (!f.txt) continue;
        const compact = compactNorm(f.txt);
        if (f.txt === nq) best = Math.max(best, 100 * f.w);
        if (f.txt.startsWith(nq)) best = Math.max(best, 80 * f.w);
        if (f.txt.includes(nq)) best = Math.max(best, 60 * f.w);
        if (cq && compact === cq) best = Math.max(best, 95 * f.w);
        if (cq && compact.startsWith(cq)) best = Math.max(best, 78 * f.w);
        if (cq && compact.includes(cq)) best = Math.max(best, 62 * f.w);
    }
    return best;
}

function haversine(a, b) {
    const R = 6371000, rad = Math.PI / 180;
    const dLat = (b.lat - a.lat) * rad, dLon = (b.lon - a.lon) * rad;
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(s));
}

function candidates() {
    return stationsState.records.map(rec => ({
        rec,
        name: rec.station.name || rec.station.nome || '',
        comune: rec.station.comune || rec.station.city || '',
        rete: stationNetworkFullLabel(rec.station),
        lat: rec.lat, lon: rec.lon,
    }));
}

// Comuni (391) dal geojson locale: cercabili ANCHE senza stazione (come v1 →
// zoom sul comune). Centroid = centro del bounding box del poligono. Lazy+cache.
let comuni = null, comuniLoading = null;
function bboxCenter(g) {
    if (!g) return null;
    let minLat = 90, maxLat = -90, minLon = 180, maxLon = -180;
    const scan = ring => ring.forEach(([lon, lat]) => {
        if (lat < minLat) minLat = lat; if (lat > maxLat) maxLat = lat;
        if (lon < minLon) minLon = lon; if (lon > maxLon) maxLon = lon;
    });
    if (g.type === 'Polygon') g.coordinates.forEach(scan);
    else if (g.type === 'MultiPolygon') g.coordinates.forEach(poly => poly.forEach(scan));
    else return null;
    return { lat: (minLat + maxLat) / 2, lon: (minLon + maxLon) / 2 };
}
function loadComuni() {
    if (comuni) return Promise.resolve(comuni);
    if (!comuniLoading) {
        comuniLoading = fetch('/v2/assets/data/sicily-municipalities.geojson', { cache: 'force-cache' })
            .then(r => r.json())
            .then(gj => {
                comuni = (gj.features || []).map(f => {
                    const p = f.properties || {};
                    const c = bboxCenter(f.geometry);
                    return c ? { name: p.municipality_name || p.comune || '', comune: '', rete: '', kind: 'comune', province: p.province_name || '', lat: c.lat, lon: c.lon } : null;
                }).filter(x => x && x.name);
                return comuni;
            })
            .catch(() => { comuni = []; return comuni; });
    }
    return comuniLoading;
}

function layerSection(control) {
    const group = control.closest('.wsv2-group');
    if (group) return group.querySelector('.wsv2-group__head')?.textContent?.trim() || 'Livelli';
    if (control.closest('#wsv2-view-options')) return 'Visualizzazione';
    return 'Livelli';
}

function layerSearchScore(query, candidate) {
    const q = norm(query);
    const name = norm(candidate.name);
    const haystack = norm(`${candidate.name} ${candidate.section} ${candidate.keywords || ''}`);
    if (!q) return 0;
    if (name === q) return 100;
    if (name.startsWith(q)) return 90;
    if (name.split(' ').some(word => word.startsWith(q))) return 80;
    if (name.includes(q)) return 70;
    if (haystack.includes(q)) return 50;
    return 0;
}

function layerCandidates() {
    const controls = [
        ...document.querySelectorAll('#layersBody .wsv2-group__body .wsv2-toggle input[type="checkbox"]'),
        ...['chkMarkers', 'chkInterpol', 'risk-firms', 'me-webcam']
            .map(id => document.getElementById(id)).filter(Boolean),
    ];
    const seen = new Set();
    const candidates = [];
    for (const control of controls) {
        if (!control.id || control.disabled || seen.has(control.id)) continue;
        const row = control.closest('.wsv2-toggle');
        const name = row?.querySelector(':scope > span')?.textContent?.trim();
        if (!row || !name) continue;
        seen.add(control.id);
        candidates.push({
            id: control.id,
            name,
            section: layerSection(control),
            keywords: control.id.replace(/[-_]/g, ' '),
            active: () => control.checked,
            activate: () => {
                const group = control.closest('.wsv2-group');
                if (group) group.open = true;
                if (!control.checked) control.click();
            },
        });
    }

    const parameter = document.getElementById('selectParametro');
    if (parameter) {
        for (const option of parameter.options) {
            const name = option.textContent.trim();
            candidates.push({
                id: `parameter-${option.value}`,
                name,
                section: 'Dato stazioni',
                keywords: `${option.value} parametro stazioni`,
                active: () => parameter.value === option.value,
                activate: () => {
                    const markers = document.getElementById('chkMarkers');
                    if (markers && !markers.checked) markers.click();
                    if (parameter.value !== option.value) {
                        parameter.value = option.value;
                        parameter.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                },
            });
        }
    }
    return candidates;
}

function initLayerSearch() {
    const panel = document.getElementById('layersPanel');
    const header = panel?.querySelector('header');
    const panelBody = document.getElementById('layersBody');
    if (!panel || !header || !panelBody || document.getElementById('btn-layer-search')) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.id = 'btn-layer-search';
    button.className = 'wsv2-iconbtn wsv2-layer-search-btn';
    button.title = 'Cerca un livello';
    button.setAttribute('aria-label', button.title);
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', 'wsv2-layer-search-wrap');
    button.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6.5"></circle><path d="m16 16 4 4"></path></svg>';
    const collapse = header.querySelector('.wsv2-collapse-all');
    collapse ? header.insertBefore(button, collapse) : header.appendChild(button);

    const wrap = document.createElement('div');
    wrap.id = 'wsv2-layer-search-wrap';
    wrap.className = 'wsv2-search wsv2-search--layers wsv2-layer-search';
    wrap.hidden = true;
    wrap.innerHTML = `
        <input type="search" id="wsv2-layer-search-input" placeholder="Cerca un livello…"
               autocomplete="off" spellcheck="false" aria-label="Cerca un livello"
               aria-controls="wsv2-layer-search-results" aria-autocomplete="list">
        <ul class="wsv2-search-results wsv2-panel" id="wsv2-layer-search-results" role="listbox" hidden></ul>`;
    panelBody.prepend(wrap);

    const input = wrap.querySelector('input');
    const results = wrap.querySelector('ul');
    let list = [];
    let activeIdx = -1;

    function closeResults() {
        results.hidden = true;
        results.replaceChildren();
        list = [];
        activeIdx = -1;
        input.removeAttribute('aria-activedescendant');
    }
    function setOpen(open) {
        wrap.hidden = !open;
        button.classList.toggle('is-open', open);
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) {
            requestAnimationFrame(() => input.focus());
        } else {
            input.value = '';
            closeResults();
        }
    }
    function pick(candidate) {
        if (!candidate) return;
        candidate.activate();
        setOpen(false);
        document.getElementById('btnLayersClose')?.click();
    }
    function render() {
        results.replaceChildren();
        if (!list.length) {
            results.hidden = true;
            input.removeAttribute('aria-activedescendant');
            return;
        }
        list.forEach((candidate, index) => {
            const item = document.createElement('li');
            item.id = `wsv2-layer-result-${index}`;
            item.role = 'option';
            item.classList.toggle('active', index === activeIdx);
            item.setAttribute('aria-selected', index === activeIdx ? 'true' : 'false');
            const name = document.createElement('strong');
            name.textContent = candidate.name;
            const meta = document.createElement('span');
            meta.textContent = `${candidate.section}${candidate.active() ? ' · Attivo' : ''}`;
            item.append(name, meta);
            item.addEventListener('pointerdown', event => {
                event.preventDefault();
                pick(candidate);
            });
            results.appendChild(item);
        });
        results.hidden = false;
        if (activeIdx >= 0) input.setAttribute('aria-activedescendant', `wsv2-layer-result-${activeIdx}`);
        else input.removeAttribute('aria-activedescendant');
    }
    function updateResults() {
        const query = input.value.trim();
        if (!query) { closeResults(); return; }
        list = layerCandidates()
            .map(candidate => ({ candidate, score: layerSearchScore(query, candidate) }))
            .filter(result => result.score > 0)
            .sort((a, b) => b.score - a.score || a.candidate.name.localeCompare(b.candidate.name, 'it'))
            .slice(0, 12)
            .map(result => result.candidate);
        activeIdx = -1;
        render();
    }

    button.addEventListener('click', () => setOpen(wrap.hidden));
    input.addEventListener('input', updateResults);
    input.addEventListener('keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); setOpen(false); button.focus(); return; }
        if (!list.length) return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            activeIdx = Math.min(activeIdx + 1, list.length - 1);
            render();
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            activeIdx = Math.max(activeIdx - 1, 0);
            render();
        } else if (event.key === 'Enter') {
            event.preventDefault();
            pick(list[activeIdx >= 0 ? activeIdx : 0]);
        }
    });
}

export function initSearch(map) {
    const wrap = document.createElement('div');
    wrap.className = 'wsv2-search wsv2-search--layers';
    wrap.innerHTML = `
        <input type="search" id="wsv2-search-input" placeholder="Cerca stazione o comune…"
               autocomplete="off" spellcheck="false" aria-label="Cerca stazione">
        <ul class="wsv2-search-results wsv2-panel" id="wsv2-search-results" role="listbox" hidden></ul>`;
    const panelBody = document.getElementById('layersBody');
    if (panelBody) panelBody.prepend(wrap);
    else document.querySelector('.wsv2-topbar .wsv2-spacer')?.replaceWith(wrap);

    const input = wrap.querySelector('input');
    const box = wrap.querySelector('ul');
    let activeIdx = -1;
    let list = [];

    function close() { box.hidden = true; box.innerHTML = ''; activeIdx = -1; list = []; }
    function closeLayersPanel() {
        const panel = document.getElementById('layersPanel');
        if (panel?.classList.contains('open')) document.getElementById('btnLayersClose')?.click();
    }

    function pick(item) {
        if (!item) return;  // touch: guardia se il race blur/tap avesse svuotato la lista
        close();
        input.blur();
        // La ricerca ora vive nel drawer: dopo la scelta lo chiudiamo, soprattutto
        // su mobile, così il flyTo e l'eventuale scheda stazione restano visibili.
        closeLayersPanel();
        // Comune (senza stazione) → zoom sul comune, niente popup stazione.
        if (item.kind === 'comune') {
            map.flyTo({ center: [item.lon, item.lat], zoom: Math.max(map.getZoom(), 11), speed: 1.6 });
            return;
        }
        map.flyTo({ center: [item.lon, item.lat], zoom: Math.max(map.getZoom(), 12), speed: 1.6 });
        map.once('moveend', () => {
            document.dispatchEvent(new CustomEvent('wsv2:station-click', { detail: { rec: item.rec } }));
        });
    }

    function render() {
        if (!list.length) { close(); return; }
        box.innerHTML = list.map((r, i) => `
            <li role="option" data-i="${i}" class="${i === activeIdx ? 'active' : ''}">
                <strong>${r.name}</strong>
                <span>${r.kind === 'comune' ? '🏙️ Comune' + (r.province ? ' · ' + r.province : '') : [r.comune, r.rete].filter(Boolean).join(' · ')}</span>
            </li>`).join('');
        box.hidden = false;
        box.querySelectorAll('li').forEach(li => {
            // pointerdown (non mousedown): su touch il mousedown è sintetizzato DOPO
            // il touchend, quando il setTimeout(close,150) del blur ha già svuotato la
            // lista → il tap non zoomava. pointerdown scatta al contatto, prima del blur.
            li.addEventListener('pointerdown', (e) => { e.preventDefault(); pick(list[Number(li.dataset.i)]); });
        });
    }

    input.addEventListener('focus', loadComuni, { once: true });
    input.addEventListener('input', () => {
        loadComuni();
        const q = input.value.trim();
        if (q.length < 2) { close(); return; }
        const c = map.getCenter();
        const center = { lat: c.lat, lon: c.lng };
        const stationHits = candidates()
            .map(st => ({ ...st, sc: scoreStation(q, st), dist: haversine(center, st) }))
            .filter(r => r.sc > 0);
        // comuni leggermente sotto le stazioni a parità di match (*0.92)
        const comuneHits = (comuni || [])
            .map(cm => ({ ...cm, sc: scoreStation(q, cm) * 0.92, dist: haversine(center, cm) }))
            .filter(r => r.sc > 0);
        list = [...stationHits, ...comuneHits]
            .sort((a, b) => b.sc - a.sc || a.dist - b.dist)
            .slice(0, 10);
        activeIdx = -1;
        render();
    });
    input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { close(); input.blur(); }
        if (!list.length) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); activeIdx = Math.min(activeIdx + 1, list.length - 1); render(); }
        if (e.key === 'ArrowUp') { e.preventDefault(); activeIdx = Math.max(activeIdx - 1, 0); render(); }
        if (e.key === 'Enter' && activeIdx >= 0) { e.preventDefault(); pick(list[activeIdx]); }
    });
    input.addEventListener('blur', () => setTimeout(close, 150));

    window.addEventListener('keydown', (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
            e.preventDefault();
            const panel = document.getElementById('layersPanel');
            if (panel && !panel.classList.contains('open')) document.getElementById('btnLayers')?.click();
            input.focus();
        }
    });

    initLayerSearch();
}
